/**
 * Validacao de documentos brasileiros.
 *
 * Fica em `shared` porque roda nos DOIS lados: o formulario avisa o erro de
 * digito antes de qualquer ida ao servidor, e a API valida de novo porque
 * validacao de cliente e conveniencia, nao garantia.
 *
 * O que estas funcoes NAO fazem: dizer se o documento existe de verdade. Um CPF
 * com digito correto pode nunca ter sido emitido. Conferir isso exige base
 * oficial, e esta na Onda 2 do risco R7 do SPEC — aqui barramos apenas o que e
 * matematicamente impossivel.
 */
export function apenasDigitos(v) {
    return v.replace(/\D/g, '');
}
// ────────────────────────────────────────────────────────────────── CPF
/**
 * CPF: 11 digitos, dois digitos verificadores modulo 11.
 *
 * Sequencias repetidas (111.111.111-11) passam na conta dos digitos e por isso
 * sao recusadas explicitamente — sao o valor que aparece quando alguem preenche
 * o campo para "passar da tela".
 */
export function cpfValido(entrada) {
    const cpf = apenasDigitos(entrada);
    if (cpf.length !== 11)
        return false;
    if (/^(\d)\1{10}$/.test(cpf))
        return false;
    for (const [tamanho, posicao] of [
        [9, 10],
        [10, 11],
    ]) {
        let soma = 0;
        for (let i = 0; i < tamanho; i++)
            soma += Number(cpf[i]) * (posicao - i);
        const resto = (soma * 10) % 11;
        const digito = resto === 10 ? 0 : resto;
        if (digito !== Number(cpf[tamanho]))
            return false;
    }
    return true;
}
export function formatarCpf(v) {
    const d = apenasDigitos(v).slice(0, 11);
    if (d.length <= 3)
        return d;
    if (d.length <= 6)
        return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9)
        return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}
// ───────────────────────────────────────────────────────────────── CNPJ
/** CNPJ: 14 digitos, dois verificadores com pesos ciclicos de 2 a 9. */
export function cnpjValido(entrada) {
    const cnpj = apenasDigitos(entrada);
    if (cnpj.length !== 14)
        return false;
    if (/^(\d)\1{13}$/.test(cnpj))
        return false;
    const calcular = (tamanho) => {
        let soma = 0;
        let peso = tamanho - 7;
        for (let i = 0; i < tamanho; i++) {
            soma += Number(cnpj[i]) * peso;
            peso = peso - 1 < 2 ? 9 : peso - 1;
        }
        const resto = soma % 11;
        return resto < 2 ? 0 : 11 - resto;
    };
    return calcular(12) === Number(cnpj[12]) && calcular(13) === Number(cnpj[13]);
}
export function formatarCnpj(v) {
    const d = apenasDigitos(v).slice(0, 14);
    if (d.length <= 2)
        return d;
    if (d.length <= 5)
        return `${d.slice(0, 2)}.${d.slice(2)}`;
    if (d.length <= 8)
        return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length <= 12)
        return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}
// ───────────────────────────────────────────────────────────────── placa
/**
 * Placa em dois formatos, os dois validos e em circulacao:
 *   antiga   ABC1234  (3 letras + 4 digitos)
 *   Mercosul ABC1D23  (3 letras + digito + letra + 2 digitos)
 *
 * Normalizamos para 7 caracteres sem separador, maiusculas — o hifen da placa
 * antiga e so apresentacao, e guardar com e sem hifen criaria duas placas para
 * o mesmo veiculo, furando o unique.
 */
const PLACA_ANTIGA = /^[A-Z]{3}\d{4}$/;
const PLACA_MERCOSUL = /^[A-Z]{3}\d[A-Z]\d{2}$/;
export function normalizarPlaca(v) {
    return v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
}
export function placaValida(entrada) {
    const p = normalizarPlaca(entrada);
    return PLACA_ANTIGA.test(p) || PLACA_MERCOSUL.test(p);
}
export function formatoDaPlaca(entrada) {
    const p = normalizarPlaca(entrada);
    if (PLACA_ANTIGA.test(p))
        return 'antiga';
    if (PLACA_MERCOSUL.test(p))
        return 'mercosul';
    return null;
}
export function formatarPlaca(v) {
    const p = normalizarPlaca(v);
    // So a placa antiga leva hifen; a Mercosul e escrita sem separador.
    if (PLACA_ANTIGA.test(p))
        return `${p.slice(0, 3)}-${p.slice(3)}`;
    return p;
}
// ─────────────────────────────────────────────────────────────── RENAVAM
/** RENAVAM: 11 digitos, um verificador modulo 11 com pesos 3,2,9,8,7,6,5,4,3,2. */
export function renavamValido(entrada) {
    const r = apenasDigitos(entrada).padStart(11, '0');
    if (r.length !== 11)
        return false;
    if (/^0{11}$/.test(r))
        return false;
    const pesos = [3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let soma = 0;
    for (let i = 0; i < 10; i++)
        soma += Number(r[i]) * pesos[i];
    const resto = (soma * 10) % 11;
    const digito = resto === 10 ? 0 : resto;
    return digito === Number(r[10]);
}
// ─────────────────────────────────────────────────────────────────── CNH
/**
 * Categorias de CNH e o que cada uma habilita a conduzir.
 *
 * Isto NAO e enfeite: a API recusa vincular um veiculo a um motorista cuja
 * categoria nao o habilita. Um bitrem exigindo categoria E entregue a um
 * motorista categoria B e um problema legal, nao um detalhe de cadastro.
 */
export const CATEGORIAS_CNH = ['A', 'B', 'C', 'D', 'E', 'AB', 'AC', 'AD', 'AE'];
/** Peso bruto total maximo (kg) que cada categoria habilita. */
const LIMITE_POR_CATEGORIA = {
    A: 0, // so motocicleta — nao serve para carga
    B: 3500,
    C: Infinity,
    D: 3500, // passageiros; para carga vale o limite de B
    E: Infinity,
    AB: 3500,
    AC: Infinity,
    AD: 3500,
    AE: Infinity,
};
export function cnhValida(numero) {
    const n = apenasDigitos(numero);
    return n.length === 11 && !/^(\d)\1{10}$/.test(n);
}
/**
 * A categoria habilita conduzir um veiculo com esta capacidade?
 *
 * Regra pratica: acima de 3.500 kg de PBT exige C ou E (ou as combinadas
 * correspondentes). Categoria A sozinha nunca habilita transporte de carga.
 */
export function categoriaHabilita(categoria, capacidadeKg, eixos) {
    const limite = LIMITE_POR_CATEGORIA[categoria.toUpperCase()];
    if (limite === undefined)
        return false;
    // Conjunto acoplado (mais de 3 eixos, tipicamente carreta e bitrem) exige E.
    if (eixos > 3)
        return ['E', 'AE'].includes(categoria.toUpperCase());
    return capacidadeKg <= limite;
}
/** Categorias que habilitam este veiculo — usado para explicar a recusa. */
export function categoriasQueHabilitam(capacidadeKg, eixos) {
    return CATEGORIAS_CNH.filter((c) => categoriaHabilita(c, capacidadeKg, eixos));
}
// ───────────────────────────────────────────────────────────────── outros
/** CEP: 8 digitos. */
export function cepValido(v) {
    return apenasDigitos(v).length === 8;
}
export function formatarCep(v) {
    const d = apenasDigitos(v).slice(0, 8);
    return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
}
/** RNTRC (registro ANTT do transportador): 8 digitos. */
export function rntrcValido(v) {
    const d = apenasDigitos(v);
    return d.length === 8 && !/^0+$/.test(d);
}
export const UFS = [
    'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT',
    'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];
export function ufValida(v) {
    return UFS.includes(v.toUpperCase());
}
//# sourceMappingURL=documentos.js.map